import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LoadPanel } from "devextreme-react";
import { useTranslation } from "react-i18next";
import { CustomDataGrid } from "../CustomDataGrid";
import {
    MOCK_REQUEST_FORMS, MOCK_FORM_FIELDS, MOCK_TICKET_TYPES,
} from "./forms-mock";
import "../messages/TicketPage.css";

type FormsTab = "requestForms" | "fields" | "ticketTypes";

// Forms menusundeki Request Forms / Fields / Ticket Types ogeleri buraya yonlenir;
// path'e gore acilacak sekme belirlenir.
function tabFromPath(pathname: string): FormsTab {
    if (pathname.endsWith("/fields")) return "fields";
    if (pathname.endsWith("/ticket-types")) return "ticketTypes";
    return "requestForms";
}

// Aktif/pasif rozeti — diger liste ekranlariyla ayni gorunum.
function StatusBadge({ active, t }: { active: boolean; t: (k: string) => string }) {
    const color = active ? "#10B981" : "#9CA3AF";
    return (
        <span style={{ background: `${color}1f`, color, fontWeight: 600, fontSize: 12, padding: "2px 10px", borderRadius: 999 }}>
            {active ? t("active") : t("passive")}
        </span>
    );
}

export default function FormsPage() {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    // Hangi liste (Request Forms / Fields / Ticket Types) acilacagi sol menuden (route) gelir.
    const tab = tabFromPath(pathname);
    const [loading] = useState(false);

    const requestFormColumns = [
        { dataField: "name", caption: t("name"), width: 220 },
        { dataField: "description", caption: t("description"), width: 320 },
        { dataField: "channel", caption: t("channel"), width: 130 },
        { dataField: "fieldCount", caption: t("fieldCount"), width: 110, alignment: "center" as const },
        {
            dataField: "isActive", caption: t("status"), width: 110, alignment: "center" as const,
            cellRender: (d: any) => <StatusBadge active={d.value !== false} t={t} />,
        },
        { dataField: "", caption: "", width: undefined, allowSorting: false, allowFiltering: false },
    ];

    const fieldColumns = [
        { dataField: "label", caption: t("fieldLabel"), width: 200 },
        {
            dataField: "key", caption: t("fieldKey"), width: 180,
            cellRender: (d: any) => <span className="font-mono text-xs" style={{ color: "var(--subtitle-text-color)" }}>{d.value}</span>,
        },
        {
            dataField: "type", caption: t("fieldType"), width: 140,
            cellRender: (d: any) => (
                <span className="font-mono text-xs" style={{ background: "var(--base-bg-darken-4)", color: "var(--base-text-color)", padding: "2px 8px", borderRadius: 6 }}>
                    {d.value}
                </span>
            ),
        },
        {
            dataField: "required", caption: t("required"), width: 120, alignment: "center" as const,
            cellRender: (d: any) => (
                <span style={{ color: d.value ? "#EF4444" : "var(--subtitle-text-color)", fontWeight: d.value ? 600 : 400 }}>
                    {d.value ? t("required") : t("optional")}
                </span>
            ),
        },
        {
            dataField: "isActive", caption: t("status"), width: 110, alignment: "center" as const,
            cellRender: (d: any) => <StatusBadge active={d.value !== false} t={t} />,
        },
        { dataField: "", caption: "", width: undefined, allowSorting: false, allowFiltering: false },
    ];

    const ticketTypeColumns = [
        {
            dataField: "name", caption: t("name"), width: 200,
            cellRender: (d: any) => (
                <span className="flex items-center gap-2">
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: d.data.color, flexShrink: 0 }} />
                    {d.value}
                </span>
            ),
        },
        {
            dataField: "code", caption: t("code"), width: 120,
            cellRender: (d: any) => (
                <span className="font-mono text-xs" style={{ fontWeight: 700, letterSpacing: "0.04em", background: "var(--base-bg-darken-4)", color: "var(--base-text-color)", padding: "2px 8px", borderRadius: 6 }}>
                    {d.value}
                </span>
            ),
        },
        {
            dataField: "slaHours", caption: t("slaHours"), width: 140, alignment: "center" as const,
            cellRender: (d: any) => <span>{d.value} {t("hours")}</span>,
        },
        {
            dataField: "isActive", caption: t("status"), width: 110, alignment: "center" as const,
            cellRender: (d: any) => <StatusBadge active={d.value !== false} t={t} />,
        },
        { dataField: "", caption: "", width: undefined, allowSorting: false, allowFiltering: false },
    ];

    const grid = (() => {
        switch (tab) {
            case "fields":
                return <CustomDataGrid key="forms-fields" className="custom-grid compact-grid" data={MOCK_FORM_FIELDS} columns={fieldColumns} keyExpr="ref" height="100%" t={t} noDataText={t("noDataText")} columnChooserEnabled filterRowVisible={false} rowAlternationEnabled={true} allowColumnReordering exportEnabled />;
            case "ticketTypes":
                return <CustomDataGrid key="forms-ticket-types" className="custom-grid compact-grid" data={MOCK_TICKET_TYPES} columns={ticketTypeColumns} keyExpr="ref" height="100%" t={t} noDataText={t("noDataText")} columnChooserEnabled filterRowVisible={false} rowAlternationEnabled={true} allowColumnReordering exportEnabled />;
            default:
                return <CustomDataGrid key="forms-request-forms" className="custom-grid compact-grid" data={MOCK_REQUEST_FORMS} columns={requestFormColumns} keyExpr="ref" height="100%" t={t} noDataText={t("noDataText")} columnChooserEnabled filterRowVisible={false} rowAlternationEnabled={true} allowColumnReordering exportEnabled />;
        }
    })();

    return (
        <div className="ticket-page-root flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <LoadPanel visible={loading} />

            <div className="flex-1 min-h-0" style={{ padding: "12px 16px 0" }}>
                {grid}
            </div>
        </div>
    );
}
